import { useColorContext } from '../../providers/ColorsProvider';
import InfoIcon from '../InfoIcon';
import { UseFormRegister, Path } from 'react-hook-form';
import './checkbox.css';
import { dynamicCheckboxCss } from './DynamicCheckboxCss';

// Update CheckboxProps to use template literal types for inputKey
interface CheckboxProps<T extends object> {
  inputKey: Path<T>;
  label?: string;
  register: UseFormRegister<T>;
  infoText?: string;
  link?: string;
  onClick?: () => void;
  labelColor?: string;
  linkColor?: string;
}

interface CheckboxLabelProps<T extends object> {
  label?: string;
  link?: string;
  inputKey?: Path<T>;
  onClick?: () => void;
  labelColor?: string;
  linkColor?: string;
}

/**
 * Checkbox component
 * @param param0  - inputKey: key of the input in the widgetState
 *              - label: label text
 *              - register: function to register the checkbox with React Hook Form
 *              - infoText: text to display when hovering over the info icon
 * @returns - checkbox component
 */
export const ControlledCheckbox = <T extends object>({ inputKey, label, register, infoText, link, onClick, labelColor, linkColor }: CheckboxProps<T>) => {
  const { buttonColor, buttonTextColor } = useColorContext();

  // dynamic style for the checkbox to modify the colors based on webshopcolors
  const dynamicStyle = dynamicCheckboxCss({ buttonColor, buttonTextColor });

  return (
    <div className='flex items-center min-[350px]:mt-0 lg:mt-2'>
      <style>{dynamicStyle}</style>
      <label className='controlled-checkbox-label' htmlFor={inputKey}>
        <input type='checkbox' {...register(inputKey)} className='controlled-checkbox' id={inputKey as string} />
        <span className='controlled-checkbox-overlay'>
          <svg xmlns='http://www.w3.org/2000/svg' className='h-3.5 w-3.5 text-white' viewBox='0 0 20 20' fill='currentColor' stroke='currentColor' stroke-width='1'>
            <path fill-rule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clip-rule='evenodd'></path>
          </svg>
        </span>
      </label>

      <CheckboxLabel<T> label={label} inputKey={inputKey} link={link} onClick={onClick} labelColor={labelColor} linkColor={linkColor} />
      {infoText && <InfoIcon className='ml-2' infoText={infoText} />}
    </div>
  );
};

const CheckboxLabel = <T extends object>({ label, link, inputKey, onClick, labelColor, linkColor }: CheckboxLabelProps<T>) => {
  // case of no label or link
  if (!label && !link) return <></>;

  // case of label and no link
  if (label && !link) {
    return (
      <label htmlFor={inputKey as string} className='text-[14px] font-normal mb-0' style={{ color: labelColor ? labelColor : '#858796' }}>
        {label}
      </label>
    );
  }

  // case of link and no label
  if (!label && link) {
    return (
      <p onClick={onClick} className='text-[14px] font-normal mb-0 underline cursor-pointer' style={{ color: linkColor ? linkColor : '#858796' }}>
        {link}
      </p>
    );
  }

  // case of label and link
  if (label && link) {
    return (
      <div className='flex items-center gap-1'>
        <label htmlFor={inputKey as string} className='text-[14px] font-normal mb-0' style={{ color: labelColor ? labelColor : '#858796' }}>
          {label}
          <span
            onClick={(event) => {
              if (!onClick) return;
              event.stopPropagation();
              event.preventDefault();
              onClick();
            }}
            className='text-[14px] inline font-normal mb-0 underline cursor-pointer'
            style={{ color: linkColor ? linkColor : '#858796' }}
          >
            {link}
          </span>
        </label>
      </div>
    );
  } else {
    return <></>;
  }
};
